import React from 'react';
import { App, Button, Card, Empty, List, Popconfirm, Space, Tag, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useOfflineQueue } from '../hooks/useOfflineQueue';
import { discardOfflineOrder, retryOfflineOrder, OfflineQueueEntry } from '../services/offlineQueue';
import { useRestaurantTimeZone } from '../hooks/useTimeZone';

const { Text, Title, Paragraph } = Typography;

/**
 * Lo que la caja hizo sin conexión: lo que falta mandar y lo que el servidor rechazó.
 *
 * Pantalla propia y no una pestaña dentro de Órdenes: es donde se ve el dinero que no
 * entró al corte, y el día que algo falla hay que poder llegar aquí directo desde el
 * aviso de la barra.
 */
const PorRevisar: React.FC = () => {
  const { message } = App.useApp();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const { pending, failed, syncing, sync } = useOfflineQueue();

  const when = (entry: OfflineQueueEntry) => formatInTimeZone(entry.occurredAt, 'DD/MM/YYYY HH:mm');

  // Se reintenta y se descarta POR ORDEN (mesa, para llevar o domicilio), nunca una
  // operación suelta: mandar el cobro sin su
  // comanda cierra la cuenta sin los productos, y el importe entero se registra como cambio.
  const failedByOrder = failed.reduce<Record<string, OfflineQueueEntry[]>>((acc, entry) => {
    (acc[entry.orderId] ||= []).push(entry);
    return acc;
  }, {});

  const handleRetry = async (orderId: string) => {
    await retryOfflineOrder(orderId);
    await sync();
  };

  const handleDiscard = async (orderId: string) => {
    await discardOfflineOrder(orderId);
    message.success('Operaciones descartadas');
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Title level={3} style={{ marginBottom: 4 }}>Operaciones sin conexión</Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Lo que esta computadora registró mientras no había internet. Lo pendiente se manda
          solo; lo rechazado necesita que alguien decida qué hacer.
        </Paragraph>
      </div>

      <Card
        title={`Rechazadas por el servidor (${failed.length})`}
        extra={<Button icon={<ReloadOutlined />} loading={syncing} onClick={sync}>Sincronizar ahora</Button>}
      >
        {failed.length === 0 ? (
          <Empty description="Nada rechazado" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <List
            dataSource={Object.entries(failedByOrder)}
            renderItem={([orderId, entries]) => (
              <List.Item
                actions={[
                  <Button key="retry" type="link" onClick={() => handleRetry(orderId)}>
                    Reintentar {entries.length > 1 ? 'todo' : ''}
                  </Button>,
                  <Popconfirm
                    key="discard"
                    title="Descartar lo de esta orden"
                    description="No se va a registrar en el sistema. Si había un cobro, hay que capturarlo a mano."
                    okText="Descartar"
                    cancelText="Cancelar"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => handleDiscard(orderId)}
                  >
                    <Button type="link" danger>Descartar</Button>
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  title={entries[0].label}
                  description={
                    <Space direction="vertical" size={2}>
                      <Text type="secondary">{when(entries[0])}</Text>
                      {entries.map((entry) => (
                        <Text key={entry.id} type="danger">{entry.error}</Text>
                      ))}
                      {entries.length > 1 && (
                        <Text type="secondary">
                          {entries.length} operaciones de esta orden; se reintentan juntas y en orden.
                        </Text>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>

      <Card title={`Pendientes de mandar (${pending.length})`}>
        {pending.length === 0 ? (
          <Empty description="Todo sincronizado" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <List
            dataSource={pending}
            renderItem={(entry) => (
              <List.Item>
                <List.Item.Meta title={entry.label} description={when(entry)} />
                <Tag color="processing">Pendiente</Tag>
              </List.Item>
            )}
          />
        )}
      </Card>
    </Space>
  );
};

export default PorRevisar;
